import {useState, useEffect, useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, Radio, FormControlLabel, RadioGroup as MuiRadioGroup, IconButton, Tooltip} from '@mui/material'
import {styled} from '@mui/material/styles'
import RemoveIcon from '@mui/icons-material/Remove'
import OutreachFilterControl from './OutreachFilterControl'
import {useOutreachFilters} from '../../../store/hooks/useOutreachFilters'
import {useRolePermissions} from '../../../hooks/useRolesPermissions'

/**
 * Styled components
 */
const GroupContainer = styled(Box)(({theme}) => ({
  width: '100%'
}))

const RadioLabelContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%'
}))

const RadioLabel = styled(FormControlLabel)(({theme}) => ({
  marginLeft: 0,
  marginRight: 0,
  flex: 1,
  '& .MuiFormControlLabel-label': {
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: theme.typography.fontWeightMedium
  }
}))

const RemoveButton = styled(IconButton)(({theme}) => ({
  padding: theme.spacing(0.5),
  marginLeft: theme.spacing(1),
  color: theme.palette.text.secondary,
  '&:hover': {
    color: theme.palette.error.main,
    backgroundColor: theme.palette.error.light + '20'
  }
}))

const FilterContainer = styled(Box)(({theme}) => ({
  marginLeft: theme.spacing(2),
  paddingLeft: theme.spacing(1),
  borderLeft: `2px solid ${theme.palette.primary.main}`
}))

/**
 * OutreachFiltersGroup - Groups multiple outreach filters with radio selection
 *
 * When multiple outreach filters are added (e.g., Benefits + Workforce):
 * - Shows radio buttons to select which one is active
 * - Only the active filter shows its sub-options
 * - Handles mutual exclusivity automatically
 *
 * When only one outreach filter is added:
 * - Shows it directly without radio (selected by default)
 */
const OutreachFiltersGroup = ({
  filters = [], // Array of filter configs that are added
  values = {}, // Object of {filterKey: filterValue}
  onFilterChange,
  onFilterRemove
}) => {
  // Get permission-aware options from hook
  const {benefitsOptionsWithState, workforceOptionsWithState} = useOutreachFilters()

  // Get user role to check if Delivery Admin
  const {isDeliveryAdmin} = useRolePermissions()
  const isDA = isDeliveryAdmin()

  // Track which filter is currently active (has radio selected)
  const [activeFilterKey, setActiveFilterKey] = useState(null)

  /**
   * Get filtered options for a filter based on permissions
   * - No permission: Hide the option completely (except SNAP for DA users)
   * - Mutual exclusivity: Disable with tooltip
   */
  const getFilteredOptions = (filterKey, originalOptions) => {
    // Get the appropriate options with state based on filter key
    let optionsWithState
    if (filterKey === 'receivingBenefits') {
      optionsWithState = benefitsOptionsWithState
    } else if (filterKey === 'workforceRecruitment') {
      optionsWithState = workforceOptionsWithState
    } else {
      // Fallback: return options as-is
      return originalOptions
    }

    // Filter and transform options
    return optionsWithState
      .filter((opt) => {
        // For Delivery Admin: Always show SNAP regardless of permission
        if (isDA) {
          return true
        }

        // Hide if no permission
        if (!opt.hasPermission) {
          return false
        }
        // Only include options that exist in original config
        return originalOptions.some((o) => o.value === opt.value)
      })
      .map((opt) => {
        // For Delivery Admin: SNAP should never be disabled
        if (isDA) {
          return {
            ...opt,
            disabled: false,
            hasPermission: true
          }
        }

        return {
          ...opt,
          // Disable if mutual exclusivity issue (has permission but disabled)
          disabled: opt.isDisabled && opt.hasPermission,
          tooltipText: opt.disabledReason
        }
      })
  }

  /**
   * Check if a filter has any enabled options (after permission filtering)
   */
  const filterHasEnabledOptions = (filterKey, originalOptions) => {
    const filteredOptions = getFilteredOptions(filterKey, originalOptions)
    return filteredOptions.length > 0
  }

  // Initialize active filter based on which one has values
  // Note: This runs before filtersWithOptions is computed in render, so we use filters here
  // and the render logic will handle showing only valid filters
  useEffect(() => {
    // If only one filter, it's automatically active
    if (filters.length === 1) {
      setActiveFilterKey(filters[0].key)
      return
    }

    // If multiple, find which one has values
    const filterWithValue = filters.find((f) => values[f.key]?.selected?.length > 0)
    if (filterWithValue) {
      setActiveFilterKey(filterWithValue.key)
    } else if (!activeFilterKey && filters.length > 0) {
      // Default to first filter if none have values
      setActiveFilterKey(filters[0].key)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, values])

  // Update active filter if current one has no enabled options
  useEffect(() => {
    if (activeFilterKey) {
      const currentFilter = filters.find((f) => f.key === activeFilterKey)
      if (currentFilter && !filterHasEnabledOptions(activeFilterKey, currentFilter.options || [])) {
        // Current filter has no enabled options, switch to first valid one
        const validFilter = filters.find((f) => filterHasEnabledOptions(f.key, f.options || []))
        if (validFilter) {
          setActiveFilterKey(validFilter.key)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilterKey, filters, benefitsOptionsWithState, workforceOptionsWithState])

  // Handle radio change
  const handleRadioChange = (event) => {
    const newActiveKey = event.target.value
    setActiveFilterKey(newActiveKey)

    // Clear value of previously active filter (mutual exclusivity)
    for (const f of filters) {
      if (f.key !== newActiveKey && values[f.key]?.selected?.length > 0) {
        onFilterChange(f.key, {selected: [], enrolledValue: 2})
      }
    }
  }

  // Handle filter value change
  const handleFilterChange = (filterKey) => (newValue) => {
    onFilterChange(filterKey, newValue)
  }

  // Handle filter removal
  const handleFilterRemove = (filterKey) => () => {
    onFilterRemove(filterKey)
  }

  // Filter to only include filters that have at least one enabled option
  const filtersWithOptions = useMemo(() => {
    return filters.filter((f) => filterHasEnabledOptions(f.key, f.options || []))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, benefitsOptionsWithState, workforceOptionsWithState])

  // If no filters with enabled options, render nothing
  if (filtersWithOptions.length === 0) {
    return null
  }

  // If only one filter with options, render it directly
  if (filtersWithOptions.length === 1) {
    const filter = filtersWithOptions[0]
    const filteredOptions = getFilteredOptions(filter.key, filter.options || [])

    return (
      <OutreachFilterControl
        label={filter.customName || filter.label || filter.name}
        description={filter.desc}
        options={filteredOptions}
        enrolledLabel={filter.enrolledLabel || 'Enrolled'}
        value={values[filter.key] || {selected: [], enrolledValue: 2}}
        onChange={handleFilterChange(filter.key)}
        onRemove={handleFilterRemove(filter.key)}
        showRemoveButton={true}
      />
    )
  }

  // Multiple filters: render with radio selection
  return (
    <GroupContainer>
      <MuiRadioGroup value={activeFilterKey || ''} onChange={handleRadioChange}>
        {filtersWithOptions.map((filter) => {
          const filteredOptions = getFilteredOptions(filter.key, filter.options || [])

          return (
            <Box key={filter.key} mb={1}>
              <RadioLabelContainer>
                <RadioLabel
                  value={filter.key}
                  control={<Radio size='small' />}
                  label={filter.customName || filter.label || filter.name}
                />
                {/* TOD: Update remve btn component */}
                <Tooltip title={`Remove ${filter.customName || filter.label || filter.name}`} placement='top'>
                  <RemoveButton
                    size='small'
                    onClick={handleFilterRemove(filter.key)}
                    aria-label={`Remove ${filter.customName || filter.label || filter.name} filter`}
                  >
                    <RemoveIcon fontSize='small' />
                  </RemoveButton>
                </Tooltip>
              </RadioLabelContainer>

              {/* Show filter control only for active filter */}
              {activeFilterKey === filter.key && (
                <FilterContainer>
                  <OutreachFilterControl
                    label='' // Label is shown in radio
                    description={filter.desc}
                    options={filteredOptions}
                    enrolledLabel={filter.enrolledLabel || 'Enrolled'}
                    value={values[filter.key] || {selected: [], enrolledValue: 2}}
                    onChange={handleFilterChange(filter.key)}
                    onRemove={null} // Remove button is on radio label
                    showRemoveButton={false}
                  />
                </FilterContainer>
              )}
            </Box>
          )
        })}
      </MuiRadioGroup>
    </GroupContainer>
  )
}

OutreachFiltersGroup.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      displayName: PropTypes.string,
      desc: PropTypes.string,
      enrolledLabel: PropTypes.string,
      options: PropTypes.array
    })
  ).isRequired,
  values: PropTypes.object,
  onFilterChange: PropTypes.func.isRequired,
  onFilterRemove: PropTypes.func.isRequired
}

export default OutreachFiltersGroup
